import { useState } from "react";
import type { CSSProperties } from "react";
import { supabase } from "../../lib/supabase";
import { colors, fontSize, fontWeight, radius, shadow, space } from "../../lib/tokens";

interface Tenant {
  id: string;
  slug: string;
  name: string;
}

interface GenerateInvoiceModalProps {
  tenants: Tenant[];
  defaultTenantId?: string;
  onClose: () => void;
  onCreated: (snapshotId: string) => void;
}

export function GenerateInvoiceModal({
  tenants,
  defaultTenantId,
  onClose,
  onCreated,
}: GenerateInvoiceModalProps) {
  const [tenantId, setTenantId] = useState(defaultTenantId ?? tenants[0]?.id ?? "");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!tenantId || !periodStart || !periodEnd) {
      setError("Tenant, período inicial e final são obrigatórios.");
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fnError } = await supabase.functions.invoke(
      "generate-billing-snapshot",
      {
        body: {
          tenant_id: tenantId,
          period_start: periodStart,
          period_end: periodEnd,
          due_date: dueDate || null,
        },
      },
    );
    setLoading(false);
    if (fnError) {
      setError(fnError.message);
      return;
    }
    onCreated((data as { snapshot_id: string }).snapshot_id);
    onClose();
  };

  const backdrop: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(14, 17, 22, 0.4)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const card: CSSProperties = {
    background: colors.white,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.md,
    boxShadow: shadow.lg,
    padding: space.xxl,
    width: 480,
    maxWidth: "90vw",
  };
  const title: CSSProperties = {
    margin: `0 0 ${space.lg}px 0`,
    fontSize: fontSize.xl,
    fontWeight: fontWeight.semibold,
    color: colors.ink,
  };
  const fieldGroup: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: space.md,
  };
  const label: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: space.xs,
    fontSize: fontSize.sm,
  };
  const labelText: CSSProperties = {
    color: colors.stone,
    fontWeight: fontWeight.medium,
  };
  const input: CSSProperties = {
    padding: space.sm,
    fontSize: fontSize.base,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    background: colors.white,
    color: colors.ink,
    fontFamily: "inherit",
  };
  const errorText: CSSProperties = {
    color: colors.error,
    fontSize: fontSize.sm,
  };
  const actions: CSSProperties = {
    display: "flex",
    gap: space.sm,
    justifyContent: "flex-end",
    marginTop: space.md,
  };
  const cancelBtn: CSSProperties = {
    padding: `${space.sm}px ${space.lg}px`,
    border: `1px solid ${colors.fog}`,
    background: "transparent",
    borderRadius: radius.sm,
    color: colors.ink,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: loading ? "not-allowed" : "pointer",
    opacity: loading ? 0.6 : 1,
  };
  const submitBtn: CSSProperties = {
    padding: `${space.sm}px ${space.lg}px`,
    background: colors.indigo,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: loading ? "not-allowed" : "pointer",
    opacity: loading ? 0.6 : 1,
  };

  return (
    <div style={backdrop}>
      <div style={card}>
        <h2 style={title}>Gerar fatura</h2>
        <div style={fieldGroup}>
          <label style={label}>
            <span style={labelText}>Tenant</span>
            <select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              style={input}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label style={label}>
            <span style={labelText}>Início do período</span>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              style={input}
            />
          </label>
          <label style={label}>
            <span style={labelText}>Fim do período</span>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              style={input}
            />
          </label>
          <label style={label}>
            <span style={labelText}>Vencimento (opcional)</span>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={input}
            />
          </label>
          {error && <div style={errorText}>{error}</div>}
          <div style={actions}>
            <button onClick={onClose} disabled={loading} style={cancelBtn}>
              Cancelar
            </button>
            <button onClick={handleSubmit} disabled={loading} style={submitBtn}>
              {loading ? "Gerando..." : "Gerar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
